/* eslint-disable class-methods-use-this */ import { UIUtilsEjsVariables } from './UIUtilsEjsVariables.ts';
import { CA } from './analytics/Analytics.ts';
import { ClientUserState } from './ClientUserState.ts';
import { ClientI18nInitiation } from './ClientI18n.ts';
import * as Sentry from '@sentry/browser';
import { UIGameSpriteHandler } from './UIGameSpriteHandler.ts';
import { UIReferralController } from './marketing/UIReferralController.ts';
import { StorageWrapper } from './StorageWrapper.ts';
import { UIDocument } from './UIDocument.ts';
import { UIVersionController } from './UIVersionController.ts';
import { UIImagePathController } from './UIEnums.ts';
import { ColonistMembershipProduct } from '../../../shared/store/ColonistMembershipProduct.ts';
import { ColonistCoinProduct } from '../../../shared/store/ColonistCoinProduct.ts';
import { FeedbackFormUtils } from './ui_popup/ui_popup_feedback_form/FeedbackFormUtils.ts';
import { ColonistOneTimePurchaseProduct } from '../../../shared/store/ColonistOneTimePurchaseProduct.ts';
import { UIServerNotificationController } from './ui_popup/ui_popup_server_notification/UIServerNotificationController.ts';
import { TabCommunication, TabCommunicationEvent } from './tab_communication/TabCommunication.ts';
import { GlobalClientVariables } from './GlobalClientVariables.ts';
import { delay } from '@colonist/utils';
import { GoogleOneTapLoader } from './GoogleOneTapLoader.ts';
import { HotjarLoader } from './HotjarLoader.ts';
import { ValueImpressionLoader } from './ValueImpressionLoader.ts';
import { AdinPlayLoader } from './AdinPlayLoader.ts';
import { ColonistGiftMembershipProduct } from '../../../shared/store/ColonistGiftMembershipProduct.ts';
import { UINotificationSendHelper } from './notifications/UINotificationSendHelper.ts';
import { Socket } from './socket/Socket.ts';
import './socket/UISocketMonitor.ts'; // adds global startSocketMonitor
import { AnalyticEventCategory, AnalyticEventType } from '../../../shared/entity_datas/EntityDatas.ts';
import { UIUrlController } from './ui_html/UIUrlController.ts';
import { initTooltips } from './ui_utils/TooltipUtils.ts';
import { AbortError } from '@colonist/types';
import { logError } from './ui_utils/Utils.ts';
import { UITurnstile } from './network/UITurnstile.ts';
import { AudioManager } from '@colonist/audio';
import { ClientPlatformRedirection } from './ClientPlatformRedirection.ts';
import { showTranslucentOverlay } from './ui_overlays/showTranslucentOverlay.ts';
export let versionController;
export let uiGameManager;
export let socket;
export let notificationController;
export let tabCommunication;
export let globalClientVariables;
export let uiManagerEvents;
export class EntryShared {
    async initializeSoundAndVersionController() {
        UIImagePathController.setVersionNumber(UIUtilsEjsVariables.versionNumber);
        EntryShared.initializeAudio();
        await CA.initLogRocket();
        CA.initializeSessionRecorder();
        versionController = new UIVersionController(StorageWrapper.getVersionNo, UIUtilsEjsVariables.versionNumber, StorageWrapper.setVersionNo);
    }
    webGLCheck() {
        UIDocument.webGLCheck();
    }
    initializeGameManager(clientSpecificGameManager) {
        uiGameManager = clientSpecificGameManager;
    }
    initializeUIManagerEvents(clientSpecificUIManagerEventHandler) {
        setUIManagerEvents(clientSpecificUIManagerEventHandler);
    }
    static initializeSocket(socketActionController, isGameActive) {
        socket = new Socket(socketActionController);
        // bad place for this, but I could not find a better location. the uiGameManager should register itself, but it doesn't know if there is an active game
        socket.controller.registerSessionEstablishedObserver(()=>{
            if (isGameActive === null || isGameActive === void 0 ? void 0 : isGameActive()) {
                uiGameManager.socketGameSend.notifyBackendClientAutoReconnected();
            }
        });
        socket.controller.registerSessionEstablishedObserver(()=>{
            notificationController === null || notificationController === void 0 ? void 0 : notificationController.removeSocketErrorBanners();
        });
    }
    static initializeNotification(notification) {
        notificationController = notification;
    }
    completeInitialization() {
        void UIServerNotificationController.displayAllPendingNotifications();
        void UIGameSpriteHandler.loadImages();
        uiGameManager.initializeSocket(socket.gameListener.gameState);
        UIDocument.debugOptions();
        // UIDocument.showTestServerPopup()
        UINotificationSendHelper.init();
        UIReferralController.init();
    }
    static async initializeEntry() {
        await ClientI18nInitiation.init();
        const userStateLoaded = await ClientUserState.init();
        if (!userStateLoaded) {
            showTranslucentOverlay({
                title: {
                    key: 'strings:popups.errors.initializationError.title'
                },
                body: {
                    key: 'strings:popups.errors.initializationError.initializationFailed'
                },
                onConfirm: ()=>window.location.reload()
            });
            const nearlyForever = 2147483647;
            await delay(nearlyForever) // TODO fix initialization so it can be stopped without this workaround
            ;
        }
        ClientPlatformRedirection.handlePlatformRedirection();
        ClientUserState.registerChangeListener(()=>{
            GoogleOneTapLoader.init();
            HotjarLoader.init();
            ValueImpressionLoader.init();
            AdinPlayLoader.init();
            this.eagerlyObtainTurnstilePreClearance();
        });
        ColonistMembershipProduct.initProducts(UIUtilsEjsVariables.discordSKUIds);
        ColonistGiftMembershipProduct.initProducts(UIUtilsEjsVariables.discordSKUIds);
        ColonistCoinProduct.initProducts(UIUtilsEjsVariables.discordSKUIds);
        ColonistOneTimePurchaseProduct.initProducts(UIUtilsEjsVariables.discordSKUIds);
        void FeedbackFormUtils.fetchShouldShowFeedbackForm();
        tabCommunication = TabCommunication.createTabCommunication();
        tabCommunication.onEvent(TabCommunicationEvent.ReloadWebsite, ()=>window.location.reload());
        initTooltips();
    }
    initializeSentry() {
        const isLocalhost = window.location.hostname === 'localhost';
        const dsn = UIUtilsEjsVariables.isUserOnMobileAppOrWeb ? UIUtilsEjsVariables.mobileAppSentryDSN : UIUtilsEjsVariables.frontendSentryDSN;
        if (isLocalhost || !dsn) {
            window.onunhandledrejection = (event)=>{
                if (event.reason instanceof AbortError) {
                    event.preventDefault() // don't log AbortErrors
                    ;
                }
            };
            return;
        }
        // eslint-disable-next-line @typescript-eslint/typedef
        const filterTransactionSpans = function(event) {
            if (event.spans == undefined) return null;
            const FIFTY_MS = 0.05;
            const TWO_FIFTY_MS = 0.250;
            event.spans = event.spans.filter((span)=>{
                if (span.timestamp == undefined) return true;
                // eslint-disable-next-line @typescript-eslint/switch-exhaustiveness-check
                switch(span.origin){
                    case 'auto.ui.browser.metrics':
                        return span.timestamp - span.start_timestamp > FIFTY_MS;
                    case 'auto.resource.browser.metrics':
                        return span.timestamp - span.start_timestamp > TWO_FIFTY_MS;
                }
                return true;
            });
            return event;
        };
        Sentry.init({
            dsn: dsn,
            release: `Colonist:v${UIUtilsEjsVariables.versionNumber}`,
            integrations: [
                Sentry.browserTracingIntegration(),
                Sentry.replayIntegration({
                    maskAllText: false,
                    blockAllMedia: false
                })
            ],
            tracesSampleRate: UIUtilsEjsVariables.frontendSentryTraceSampleRate,
            replaysSessionSampleRate: 0.0005,
            replaysOnErrorSampleRate: 0.0005,
            allowUrls: [
                window.location.hostname
            ],
            environment: UIUtilsEjsVariables.frontendSentryEnvironment,
            ignoreErrors: [
                /^AbortError$/
            ],
            beforeSend: (event, hint)=>{
                var _event_exception_values_, _event_exception_values, _event_exception, _ClientUserState_userState, _ClientUserState_userState1;
                var _event;
                const errorMessage = event.message || ((_event_exception = event.exception) === null || _event_exception === void 0 ? void 0 : (_event_exception_values = _event_exception.values) === null || _event_exception_values === void 0 ? void 0 : (_event_exception_values_ = _event_exception_values[0]) === null || _event_exception_values_ === void 0 ? void 0 : _event_exception_values_.value);
                if (errorMessage === 'Failed to start the audio device') {
                    return null;
                }
                var _user;
                (_user = (_event = event).user) !== null && _user !== void 0 ? _user : _event.user = {};
                if ((_ClientUserState_userState = ClientUserState.userState) === null || _ClientUserState_userState === void 0 ? void 0 : _ClientUserState_userState.id) event.user.id = ClientUserState.userState.id;
                if ((_ClientUserState_userState1 = ClientUserState.userState) === null || _ClientUserState_userState1 === void 0 ? void 0 : _ClientUserState_userState1.username) event.user.username = ClientUserState.userState.username;
                return event;
            },
            beforeSendTransaction: filterTransactionSpans
        });
    }
    static sendPageVisitAnalyticEvent(pathName) {
        const resolvedPathName = pathName !== null && pathName !== void 0 ? pathName : UIUrlController.getPathnameWithoutLanguagePrefix();
        const event = {
            type: AnalyticEventType.PageVisit,
            category: AnalyticEventCategory.PageVisit,
            stringValue: resolvedPathName
        };
        void CA.sendAnalyticEvent(event);
    }
    static eagerlyObtainTurnstilePreClearance() {
        if (UIUtilsEjsVariables.isUserOnDiscord) return; // doesn't support/require turnstile
        const userWillBeCreatedForActions = ClientUserState.userState == undefined && !ClientUserState.isStale();
        if (!userWillBeCreatedForActions) return;
        void UITurnstile.obtainPreClearanceCookie();
    }
    static initializeAudio() {
        const volume = StorageWrapper.getEffectiveVolume();
        void AudioManager.init(volume);
    }
    constructor(){
        this.initializeSentry();
        globalClientVariables = new GlobalClientVariables();
        void registerServiceWorker();
        EntryShared.sendPageVisitAnalyticEvent();
    }
}
export function setUIManagerEvents(handler) {
    uiManagerEvents = handler;
}
async function registerServiceWorker() {
    if ('serviceWorker' in navigator) {
        try {
            const serviceWorkerLocation = `${UIUtilsEjsVariables.isUserOnDiscord ? '/.proxy/' : '/'}service-worker${UIUtilsEjsVariables.isUserOnMobile ? '?mobile' : ''}`;
            await navigator.serviceWorker.register(serviceWorkerLocation);
        } catch (error) {
            logError('registerServiceWorker', [
                `Service Worker registration failed with error: '${error}'`
            ]);
        }
    }
}
